import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { HomeAPI } from '@/services/residents/api/HomeAPI';
import { RoommateItem, RoommateCoverPhoto, RoommatesResponse } from '@/types/residents/roommates';

export interface RoommatesState {
  roommates: RoommateItem[]; // current roommates list
  coverPhotos: RoommateCoverPhoto[];
  isLoading: boolean;
  error: string | null;
}

const initialState: RoommatesState = {
  roommates: [],
  coverPhotos: [],
  isLoading: false,
  error: null,
};

export const fetchRoommates = createAsyncThunk<RoommatesResponse, void, { rejectValue: string }>('roommates/fetchRoommates', async (_, { rejectWithValue }) => {
  try {
    const resp = await HomeAPI.getRoommates();
    // Expected iOS shape: { roommatesCurrent: Roommate[], coverPhotos: [{ photoURL }] }
    return resp || {};
  } catch (e: any) {
    return rejectWithValue(e?.message || 'Failed to fetch roommates');
  }
});

const roommatesSlice = createSlice({
  name: 'roommates',
  initialState,
  reducers: {
    clearRoommatesError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoommates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoommates.fulfilled, (state, action: PayloadAction<RoommatesResponse>) => {
        state.isLoading = false;
        const payload = action.payload || {} as RoommatesResponse;
        const current = Array.isArray(payload.roommatesCurrent)
          ? payload.roommatesCurrent
          : Array.isArray(payload.roommates)
            ? payload.roommates
            : (Array.isArray(payload) ? payload : []);
        state.roommates = current;
        const photos = Array.isArray(payload.coverPhotos) ? payload.coverPhotos : [];
        state.coverPhotos = photos;
      })
      .addCase(fetchRoommates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch roommates';
      });
  },
});

export const { clearRoommatesError } = roommatesSlice.actions;
export default roommatesSlice.reducer;


